import React, { useEffect, useState } from "react";

import { FileToUpload, UploadPhase } from "../../../../api/media-library";
import { formatFileSize } from "../../../../utils/file-size";
import { Button } from "../../../../components/ui/button";
import { Progress } from "../../../../components/ui/progress";
import { cn } from "../../../../lib/utils";

import { UploadBatchSummary, UploadingFile } from "../../uploading-files.model";

interface Props {
  uploads: UploadingFile[];
  summary: UploadBatchSummary;
  destination?: string;
  onCancel(id: string): void;
  onRetry(id: string): void;
  onDone(): void;
}

const CANCELLABLE_PHASES = new Set<UploadPhase>([
  "queued",
  "uploading",
  "processing",
]);

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded";

export const UploadBatchList: React.FC<Props> = ({
  uploads,
  summary,
  destination,
  onCancel,
  onRetry,
  onDone,
}) => {
  const isUploadStage = summary.stage === "uploading";
  const endedWithFailures = summary.stage === "done" && summary.failedCount > 0;

  return (
    <div className="flex min-h-0 w-full flex-col gap-3">
      <div className="shrink-0 space-y-1.5">
        <div className="flex items-baseline justify-between gap-4">
          <p
            aria-hidden="true"
            className="text-sm font-medium leading-5 text-neutral-700"
          >
            {summary.label}
          </p>
          {destination && (
            <p className="min-w-0 truncate text-xs text-neutral-500">
              into <span className="text-neutral-700">{destination}</span>
            </p>
          )}
        </div>
        {/* Coarse text so a screen reader is not read every percentage tick. */}
        <p role="status" className="sr-only">
          {summary.stage === "uploading"
            ? `Uploading ${summary.uploadedCount} of ${summary.total - summary.failedCount} files`
            : summary.label}
        </p>
        <Progress
          value={summary.percent}
          className={cn(
            "h-2 rounded-full",
            summaryTrackClass(isUploadStage, endedWithFailures)
          )}
          indicatorClassName={summaryIndicatorClass(
            isUploadStage,
            endedWithFailures
          )}
        />
      </div>

      <ul
        tabIndex={0}
        aria-label="Files being uploaded"
        className={cn(
          "min-h-0 flex-1 divide-y divide-neutral-100 overflow-y-auto",
          FOCUS_RING
        )}
      >
        {uploads.map((upload) => (
          <UploadBatchRow
            key={upload.id}
            upload={upload}
            onCancel={onCancel}
            onRetry={onRetry}
          />
        ))}
      </ul>

      {summary.stage === "done" && summary.failedCount > 0 && (
        <div className="flex shrink-0 justify-end">
          <Button size="sm" variant="outline" onClick={onDone}>
            Done
          </Button>
        </div>
      )}
    </div>
  );
};

function UploadBatchRow({
  upload,
  onCancel,
  onRetry,
}: {
  upload: UploadingFile;
  onCancel(id: string): void;
  onRetry(id: string): void;
}) {
  const isFailed = upload.phase === "failed";
  const isProcessing = upload.phase === "processing";
  const isSaving = upload.phase === "saving";
  const size = formatFileSize(upload.upload.total);

  return (
    <li className="flex h-7 items-center gap-2 text-xs leading-4">
      <UploadRowThumbnail upload={upload} />

      <span
        className="min-w-0 flex-1 truncate text-neutral-700"
        title={upload.name}
      >
        {upload.name}
      </span>

      {size && <span className="shrink-0 text-neutral-500">{size}</span>}

      <div className="w-20 shrink-0" aria-hidden="true">
        <Progress
          value={isProcessing ? upload.processing.value : upload.upload.value}
          className={cn(
            "h-1 rounded-full",
            isFailed ? "bg-red-100" : "bg-neutral-200"
          )}
          indicatorClassName={rowIndicatorClass(upload)}
          indeterminate={isSaving}
        />
      </div>

      <span
        className={cn(
          "w-40 shrink-0 truncate text-right",
          isFailed ? "text-red-600" : "text-neutral-600"
        )}
        title={isFailed ? formatError(upload.error) : undefined}
      >
        {statusText(upload)}
      </span>

      {isFailed ? (
        <button
          type="button"
          aria-label={`Retry upload of ${upload.name}`}
          onClick={() => onRetry(upload.id)}
          className={cn(
            "shrink-0 cursor-pointer text-red-600 underline underline-offset-2",
            FOCUS_RING
          )}
        >
          Retry
        </button>
      ) : (
        <button
          type="button"
          aria-label={`Cancel upload of ${upload.name}`}
          disabled={!CANCELLABLE_PHASES.has(upload.phase)}
          onClick={() => onCancel(upload.id)}
          className={cn(
            "shrink-0 cursor-pointer text-neutral-500 hover:text-neutral-700 disabled:invisible",
            FOCUS_RING
          )}
        >
          <i className="fa-regular fa-xmark" />
        </button>
      )}
    </li>
  );
}

function summaryTrackClass(isUploadStage: boolean, hasFailures: boolean) {
  if (hasFailures) {
    return "bg-red-100";
  }

  return isUploadStage ? "bg-neutral-200" : "bg-green-100";
}

function summaryIndicatorClass(isUploadStage: boolean, hasFailures: boolean) {
  if (hasFailures) {
    return "bg-red-500";
  }

  return isUploadStage ? "bg-blue-500" : "bg-green-500";
}

function rowIndicatorClass(upload: UploadingFile): string {
  switch (upload.phase) {
    case "failed":
      return "bg-red-500";
    case "processing":
    case "saving":
    case "ready":
      return "bg-green-500";
    default:
      return "bg-blue-500";
  }
}

function UploadRowThumbnail({ upload }: { upload: UploadingFile }) {
  const previewUrl = useLocalPreviewUrl(upload.file);
  const url = upload.result?.thumbnail || previewUrl;

  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="size-5 shrink-0 rounded object-cover bg-neutral-100"
      />
    );
  }

  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded bg-neutral-100 text-[10px] text-neutral-400">
      <i
        className={
          upload.type === "video" ? "fa-regular fa-film" : "fa-regular fa-file"
        }
      />
    </span>
  );
}

function useLocalPreviewUrl(
  file: FileToUpload | undefined
): string | undefined {
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    if (!file || file.type !== "image") {
      setPreviewUrl(undefined);
      return;
    }

    if (file.thumbnail) {
      setPreviewUrl(file.thumbnail);
      return;
    }

    if (file.contentType === "url") {
      setPreviewUrl(file.url);
      return;
    }

    if (file.contentType !== "blob") {
      setPreviewUrl(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file.blob);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return previewUrl;
}

function statusText(upload: UploadingFile): string {
  switch (upload.phase) {
    case "queued":
      return "Queued";
    case "uploading":
      return `Uploading ${Math.round(upload.upload.value)}%`;
    case "processing":
      return `Processing ${Math.round(upload.processing.value)}%`;
    case "saving":
      return "Saving…";
    case "ready":
      return "Ready";
    case "failed":
      return `Failed · ${formatError(upload.error)}`;
  }
}

function formatError(error: unknown): string {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Upload error";
}
